import type { Goal2AggregateReportResult } from "@reminder-tracker/backend-contracts";
import {
  goal2AggregateBreakdowns,
  goal2AggregateReports,
  goal2LatencySummary,
  goal2TechnicalReliability,
  type Goal2AggregateBreakdown,
  type Goal2AggregateReport,
} from "@reminder-tracker/domain";
import { useAction, useQuery } from "convex/react";
import { useState } from "react";

import { getGoal2AdministrationCapabilities, getGoal2MonthlyReport } from "../../backend/functions";
import { AdministrationWorkSurface } from "../../ui/administration-surface";

function key() {
  return "corr_" + crypto.randomUUID().replaceAll("-", "");
}

function previousUtcMonth() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
    .toISOString()
    .slice(0, 7);
}

const reportLabels: Readonly<Record<Goal2AggregateReport, string>> = {
  "bundle-adoption": "Bundle adoption",
  "bundle-selection": "Bundle selection size",
  "draft-lifecycle": "Draft lifecycle",
  "completion-activity": "Reminder completion activity",
  "continuation-adoption": "Optional next-reminder choices",
  "bundle-save-quality": "Bundle save quality",
  "delivery-outcomes": "Delivery outcomes",
  "recovery-quality": "Recovery and latency quality",
};

const breakdownLabels: Readonly<Record<Goal2AggregateBreakdown, string>> = {
  none: "No breakdown",
  market: "Catalogue market",
  "broad-category": "Broad category",
  subcategory: "Subcategory",
  "service-type": "Service type",
  "bundle-version": "Bundle version",
  origin: "Origin",
  platform: "Platform",
  "application-release": "Application release",
};

export function Goal2ReportResultView({
  result,
}: Readonly<{ result: Goal2AggregateReportResult }>): React.JSX.Element {
  const statusCopy = {
    open: "This month is still open. Its fixed report is not ready yet.",
    preparing: "The completed month is being prepared in bounded steps.",
    "not-collected": "This metric was not collected for the whole requested period.",
    incomplete: "Collection was incomplete, so no partial figures are presented as final.",
    expired: "This fixed report has reached its retention date and is no longer available.",
    ready: "This fixed, privacy-protected monthly report is ready.",
  }[result.state];
  const latency =
    result.report === "recovery-quality" && result.state === "ready"
      ? goal2LatencySummary(result.cells)
      : null;
  const protectedValue = (key: string) => {
    const cell = result.cells.find((candidate) => candidate.key === key);
    return cell === undefined ? 0 : cell.state === "value" ? cell.value : null;
  };
  const reliabilityInputs =
    result.report === "bundle-save-quality" && result.state === "ready"
      ? [
          protectedValue("technical-success"),
          protectedValue("technical-failure"),
          protectedValue("validation-refusal"),
          protectedValue("authorization-refusal"),
          protectedValue("stale-version-refusal"),
          protectedValue("pending"),
          protectedValue("unknown"),
        ]
      : null;
  const reliability =
    reliabilityInputs !== null && reliabilityInputs.every((value) => value !== null)
      ? goal2TechnicalReliability({
          technicalSuccesses: reliabilityInputs[0]!,
          technicalFailures: reliabilityInputs[1]!,
          validationRefusals: reliabilityInputs[2]!,
          authorizationRefusals: reliabilityInputs[3]!,
          staleVersionRefusals: reliabilityInputs[4]!,
          unresolved: reliabilityInputs[5]! + reliabilityInputs[6]!,
        })
      : null;
  return (
    <section className="push-aggregate-group" aria-labelledby="goal2-report-result-heading">
      <h4 id="goal2-report-result-heading">
        {reportLabels[result.report]} · {result.period}
      </h4>
      <p role="status">{statusCopy}</p>
      <p>
        Metric {result.metricVersion} · {breakdownLabels[result.breakdown]}. Figures describe
        aggregate activity, never a person, household or reminder.
      </p>
      {latency === null ? null : latency.state === "value" ? (
        <p>
          {latency.sampleCount} protected latency samples. Approximate p50: {latency.approximateP50}
          . Approximate p95: {latency.approximateP95}.
        </p>
      ) : (
        <p>Latency percentile summary: {latency.message}.</p>
      )}
      {reliability?.ratio === null || reliability === null ? null : (
        <p>
          Technical reliability:{" "}
          {new Intl.NumberFormat("en-GB", { style: "percent" }).format(reliability.ratio)} (
          {reliability.numerator} of {reliability.denominator} terminal technical outcomes).{" "}
          {reliability.excludedRefusals} correct refusals and {reliability.unresolved} pending or
          unknown outcomes are excluded.
        </p>
      )}
      {result.cells.length > 0 ? (
        <table className="timeline">
          <thead>
            <tr>
              <th>Measure</th>
              <th>Protected result</th>
            </tr>
          </thead>
          <tbody>
            {result.cells.map((cell) => (
              <tr key={cell.key}>
                <td data-label="Measure">{cell.key.replaceAll("-", " ")}</td>
                <td data-label="Protected result">
                  {cell.state === "value"
                    ? String(cell.value) +
                      (cell.denominator === null ? "" : " of " + String(cell.denominator))
                    : cell.message}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <p>
        Retained until{" "}
        {new Intl.DateTimeFormat("en-GB", { dateStyle: "long", timeZone: "UTC" }).format(
          result.expiresAtEpochMilliseconds,
        )}
        .
      </p>
    </section>
  );
}

export function Goal2ProtectedReports(): React.JSX.Element | null {
  const [capabilityCorrelationId] = useState(key);
  const projection = useQuery(getGoal2AdministrationCapabilities, {
    correlationId: capabilityCorrelationId,
  });
  const readReport = useAction(getGoal2MonthlyReport);
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState<Goal2AggregateReport>("bundle-adoption");
  const [period, setPeriod] = useState(previousUtcMonth);
  const [breakdown, setBreakdown] = useState<Goal2AggregateBreakdown>("none");
  const [result, setResult] = useState<Goal2AggregateReportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (projection === undefined) return <p role="status">Checking protected report access…</p>;
  if (
    !projection.capabilities.some(
      (item) => item.capability === "intelligence:read-protected" && item.granted,
    )
  )
    return null;
  if (!open)
    return (
      <button type="button" onClick={() => setOpen(true)}>
        View protected Goal 2 reports
      </button>
    );
  return (
    <AdministrationWorkSurface
      id="goal2-reports-heading"
      title="Protected consumer lifecycle reports"
      purpose="Choose one fixed report, one UTC month and at most one approved breakdown."
      state={{
        label: result === null ? "Choose a report" : result.state.replaceAll("-", " "),
        tone: result === null ? "neutral" : result.state === "ready" ? "healthy" : "monitor",
      }}
      consequence="This aggregate evidence supports catalogue and lifecycle planning only. It cannot identify or open a person, household or reminder."
    >
      <button type="button" onClick={() => setOpen(false)}>
        Back to catalogue
      </button>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void readReport({ report, period, breakdown, correlationId: key() })
            .then(setResult)
            .catch(() =>
              setError("We couldn't read this protected report. Check the period and try again."),
            )
            .finally(() => setBusy(false));
        }}
      >
        <label>
          Report
          <select
            value={report}
            onChange={(event) => setReport(event.target.value as Goal2AggregateReport)}
          >
            {goal2AggregateReports.map((value) => (
              <option key={value} value={value}>
                {reportLabels[value]}
              </option>
            ))}
          </select>
        </label>
        <label>
          UTC month
          <input
            type="month"
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
            required
          />
        </label>
        <label>
          One breakdown
          <select
            value={breakdown}
            onChange={(event) => setBreakdown(event.target.value as Goal2AggregateBreakdown)}
          >
            {goal2AggregateBreakdowns.map((value) => (
              <option key={value} value={value}>
                {breakdownLabels[value]}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={busy}>
          {busy ? "Reading protected report…" : "Read fixed report"}
        </button>
      </form>
      {error === null ? null : <p role="alert">{error}</p>}
      {result === null ? null : <Goal2ReportResultView result={result} />}
    </AdministrationWorkSurface>
  );
}
